import { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { toast } from 'react-toastify';
import {
  getUserProfileDetails,
  updateNotificationDetails,
} from 'src/services/client';
import {
  NoteNotification,
  USER_ROLE_NAMES,
  UserProfileSettings,
} from '../types';

export const useNotificationSettings = () => {
  const user: any = useSelector(selectAuthenticatedUser);

  const [settings, setSettings] = useState<UserProfileSettings | null>(null);
  const [savedSettings, setSavedSettings] =
    useState<UserProfileSettings | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const userRoleName: any = user?.roles?.[0]?.role?.name;

  const isHomeOwnerOrOccupant: boolean =
    userRoleName === USER_ROLE_NAMES.HOME_OWNER ||
    userRoleName === USER_ROLE_NAMES.OCCUPANT;

  useEffect(() => {
    const fetchSettings = async () => {
      if (!user?.id) return;
      setIsLoading(true);
      try {
        const response: any = await getUserProfileDetails(user.id);

        if (response.status === 200 || response.ok) {
          const data: any = await response.json();

          if (data.noteNotification === null) {
            data.noteNotification = {
              serviceRequests: false,
              properties: false,
              suites: false,
              assets: false,
              inventoryItems: false,
              contacts: false,
            };
          }
          setSettings(data);
          setSavedSettings(data);
        }
      } catch (error) {
        console.error('Failed to fetch notification settings', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, [user?.id]);

  const toggleAllowEmail = useCallback(() => {
    setSettings((prev) =>
      prev ? { ...prev, allowEmail: !prev.allowEmail } : null
    );
  }, []);

  const toggleAllowStatus = useCallback(() => {
    setSettings((prev) =>
      prev ? { ...prev, allowStatus: !prev.allowStatus } : null
    );
  }, []);

  const toggleAllowNotes = useCallback(() => {
    setSettings((prev) =>
      prev ? { ...prev, allowNotes: !prev.allowNotes } : null
    );
  }, []);

  const toggleNoteNotification = useCallback(
    (field: keyof NoteNotification) => {
      setSettings((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          noteNotification: {
            ...prev.noteNotification,
            [field]: !prev.noteNotification[field],
          },
        };
      });
    },
    []
  );

  const isDirty: boolean =
    JSON.stringify(settings) !== JSON.stringify(savedSettings);

  const handleSave = async () => {
    if (!settings || !user?.id) return;

    setIsLoading(true);
    const { allowEmail, allowStatus, allowNotes, noteNotification } = settings;

    const data = {
      UserId: user.id,
      allowEmail,
      allowStatus,
      allowNotes,
      noteNotification: allowNotes
        ? noteNotification
        : {
            serviceRequests: false,
            properties: false,
            suites: false,
            assets: false,
            inventoryItems: false,
            contacts: false,
          },
    };

    try {
      const response: any = await updateNotificationDetails(data);

      if (response.status === 200 || response.ok) {
        setSavedSettings(settings);
        toast.success('ⓘ Notification details updated.', {
          autoClose: 10000,
          position: 'top-center',
        });
      } else {
        toast.error("Couldn't update notification details.");
      }
    } catch (error) {
      toast.error('Something went wrong.');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    settings,
    isLoading,
    isDirty,
    isHomeOwnerOrOccupant,
    toggleAllowEmail,
    toggleAllowStatus,
    toggleAllowNotes,
    toggleNoteNotification,
    handleSave,
  };
};
